import { Button, Icon, Textarea, cn } from "@nexus/ui";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { HighlightText } from "@/features/opportunities/components/HighlightText";

export interface PersonaScriptTitleEditorProps {
  title: string;
  canEdit: boolean;
  isEditing?: boolean;
  isFlashing?: boolean;
  isSaving?: boolean;
  editAria?: string;
  highlightQuery?: string;
  titleClassName?: string;
  /** Read-mode wrapper, e.g. a negative margin to align the padded title with the content edge. */
  className?: string;
  onStartEdit?: () => void;
  onStopEdit?: () => void;
  onSave: (next: string) => void | boolean | Promise<void | boolean>;
}

function finishEdit(saved: void | boolean | Promise<void | boolean>, close: () => void) {
  if (saved instanceof Promise) {
    void saved.then(
      (ok) => {
        if (ok !== false) close();
      },
      () => undefined,
    );
    return;
  }
  close();
}

function fitTitleField(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

export function PersonaScriptTitleEditor({
  title,
  canEdit,
  isEditing = false,
  isFlashing = false,
  isSaving = false,
  editAria,
  highlightQuery = "",
  titleClassName = "text-sm font-semibold leading-snug",
  className,
  onStartEdit,
  onStopEdit,
  onSave,
}: PersonaScriptTitleEditorProps) {
  const { t } = useTranslation();
  const titleAria =
    editAria ?? t("personaOpportunities.scripts.cell.editTitleAria", "Edit script title");
  const [isHovered, setIsHovered] = useState(false);
  const [draft, setDraft] = useState(title);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(title);
  }, [isEditing, title]);

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (el) fitTitleField(el);
  }, [isEditing, draft]);

  function startEdit(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (!canEdit || isSaving) return;
    setDraft(title);
    onStartEdit?.();
  }

  function cancelEdit() {
    setDraft(title);
    onStopEdit?.();
  }

  function commitEdit() {
    if (!canEdit || isSaving) return;
    if (draft === title) {
      onStopEdit?.();
      return;
    }
    finishEdit(onSave(draft), () => onStopEdit?.());
  }

  if (isEditing) {
    return (
      <div
        className="flex w-full min-w-0 items-center gap-2"
        onClick={(event) => event.stopPropagation()}
      >
        <Textarea
          ref={textareaRef}
          value={draft}
          rows={1}
          disabled={isSaving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
              return;
            }
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              commitEdit();
            }
          }}
          className={cn(
            "min-h-0 flex-1 resize-none overflow-hidden border-[1.5px] border-primary px-2 py-1.5 text-foreground shadow-none focus-visible:border-primary focus-visible:ring-0",
            titleClassName,
          )}
          aria-label={titleAria}
        />
        <div className="flex shrink-0 items-center gap-1">
          <Button type="button" size="xs" disabled={isSaving} onClick={commitEdit}>
            {t("personaOpportunities.scripts.cell.save", "Save")}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="secondary"
            disabled={isSaving}
            onClick={cancelEdit}
          >
            {t("personaOpportunities.scripts.cell.cancel", "Cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex w-fit max-w-full min-w-0 items-center gap-1.5 px-2 py-1",
        isFlashing && "persona-script-cell-flash rounded-sm",
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {canEdit ? (
        <button
          type="button"
          className={cn(
            "min-w-0 cursor-text p-0 text-left text-foreground",
            titleClassName,
          )}
          aria-label={titleAria}
          onClick={startEdit}
        >
          <HighlightText text={title} query={highlightQuery} />
        </button>
      ) : (
        <p className={cn("min-w-0 text-foreground", titleClassName)}>
          <HighlightText text={title} query={highlightQuery} />
        </p>
      )}
      {canEdit ? (
        <button
          type="button"
          className={cn(
            "grid shrink-0 place-items-center rounded-sm text-muted-foreground hover:text-foreground",
            "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-5 [@media(hover:hover)_and_(pointer:fine)]:min-w-5",
            isHovered
              ? "opacity-100"
              : "pointer-events-none opacity-0 group-hover/script:pointer-events-auto group-hover/script:opacity-100",
          )}
          title={t("personaOpportunities.scripts.cell.editTitle", "Edit title")}
          aria-label={t("personaOpportunities.scripts.cell.editTitle", "Edit title")}
          onClick={startEdit}
        >
          <Icon name="pencil" size="xs" color="inherit" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
